/**
 * Upload foto intervento via MinIO presigned URL (flusso a 2 fasi).
 *
 * 1. POST /interventi/{id}/media → ottiene presigned PUT
 * 2. PUT diretto a MinIO con il blob
 * 3. POST /interventi/media/conferma → record diventa "uploaded"
 */
import client from '@/api/client'

export interface UploadFotoArgs {
  interventoId: string
  blob: Blob
  filename: string
  tag?: string | null
  descrizione?: string
  tipo?: string
  tipo_documento?: string | null
}

function base64ToBlob(base64: string, mimeType: string): Blob {
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return new Blob([bytes], { type: mimeType })
}

export async function uploadFotoBase64(args: {
  interventoId: string
  base64: string
  mimeType: string
  tag?: string | null
}): Promise<void> {
  const blob = base64ToBlob(args.base64, args.mimeType)
  const ext = args.mimeType.split('/')[1] || 'jpg'
  await uploadFoto({
    interventoId: args.interventoId,
    blob,
    filename: `foto-${Date.now()}.${ext}`,
    tag: args.tag ?? null,
  })
}

export async function uploadFoto(args: UploadFotoArgs): Promise<void> {
  // Fase 1: presigned URL
  const init = await client.post(`/interventi/${args.interventoId}/media`, {
    tipo: args.tipo || 'foto',
    filename: args.filename,
    tag: args.tag ?? null,
    descrizione: args.descrizione ?? null,
    tipo_documento: args.tipo_documento ?? null,
  })
  const { id: mediaId, presigned_url } = init.data as { id: string; presigned_url: string }

  // Fase 2: PUT diretto su MinIO (no withCredentials, no auth header — è un signed URL)
  const putRes = await fetch(presigned_url, {
    method: 'PUT',
    body: args.blob,
    headers: { 'Content-Type': args.blob.type || 'application/octet-stream' },
  })
  if (!putRes.ok) {
    throw new Error(`Upload MinIO fallito: ${putRes.status} ${putRes.statusText}`)
  }

  // Fase 3: conferma
  await client.post('/interventi/media/conferma', { media_id: mediaId })
}
